import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { fechaDelPadron, mismaEmpresa } from './padron'
import type { BillingProfile, PadronRuc, TaxDocType } from './types'
import { ApiException, api } from '../../api/client'
import type { UpdateTenantBody } from '../../api/types.gen'
import { Banner, Button, Card, Field, cx } from '../../ui'

/**
 * A quién se le emite el comprobante.
 *
 * La web pública promete «factura electrónica si nos das tu RUC, o boleta si no
 * lo tienes», y hasta ahora no había ningún sitio donde darlo: el endpoint
 * `PATCH /tenant` aceptaba los campos desde F0 y ningún formulario los mandaba.
 *
 * Y desde #166 no es opcional: el comprobante lo emite ChambaTotal, que no lo
 * hace sin saber a quién. Sin RUC ni DNI, el comprobante espera.
 */
export function BillingProfileCard({ profile }: { profile: BillingProfile }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)

  if (!editing) {
    return (
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-subhead font-medium text-ink">Datos para el comprobante</h2>

            {profile.tax_doc_number !== null && profile.tax_doc_number !== '' ? (
              <div className="mt-3">
                <p className="text-body font-medium text-ink">{profile.legal_name}</p>
                <p className="mt-0.5 text-body text-ink-muted" data-numeric>
                  {profile.tax_doc_type} {profile.tax_doc_number}
                </p>
                {profile.billing_email !== null && profile.billing_email !== '' && (
                  <p className="mt-0.5 text-caption text-ink-subtle">{profile.billing_email}</p>
                )}
              </div>
            ) : (
              <p className="mt-2 max-w-prose text-body text-ink-muted">
                Sin datos fiscales, y los necesitamos para emitir tu comprobante: con RUC, factura;
                con DNI, boleta a tu nombre.
              </p>
            )}
          </div>

          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            {profile.tax_doc_number !== null && profile.tax_doc_number !== '' ? 'Cambiar' : 'Añadir'}
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <ProfileForm
      profile={profile}
      onDone={() => {
        void queryClient.invalidateQueries({ queryKey: ['billing'] })
        setEditing(false)
      }}
      onCancel={() => setEditing(false)}
    />
  )
}

const DOC_TYPES: { id: TaxDocType; label: string; hint: string }[] = [
  { id: 'RUC', label: 'RUC', hint: 'Se emite factura' },
  { id: 'DNI', label: 'DNI', hint: 'Se emite boleta' },
  { id: 'CE', label: 'Carné de extranjería', hint: 'Se emite boleta' },
  { id: 'PAS', label: 'Pasaporte', hint: 'Se emite boleta' },
]

/**
 * Se monta y desmonta con la edición en vez de reiniciar su estado, para que
 * los campos nazcan con lo que hay guardado sin sincronizarlo en un efecto.
 */
function ProfileForm({
  profile,
  onDone,
  onCancel,
}: {
  profile: BillingProfile
  onDone: () => void
  onCancel: () => void
}) {
  const [docType, setDocType] = useState<TaxDocType>(profile.tax_doc_type ?? 'RUC')
  const [docNumber, setDocNumber] = useState(profile.tax_doc_number ?? '')
  const [legalName, setLegalName] = useState(profile.legal_name ?? '')
  const [email, setEmail] = useState(profile.billing_email ?? '')
  const [address, setAddress] = useState(profile.billing_address ?? '')
  const [failure, setFailure] = useState<string | null>(null)

  // El RUC peruano tiene 11 dígitos y el DNI 8. Comprobarlo aquí evita mandar
  // un número que SUNAT rechazaría meses después, al emitir.
  const expected = docType === 'RUC' ? 11 : docType === 'DNI' ? 8 : null
  const digitsOnly = docType === 'RUC' || docType === 'DNI'
  const wrongLength =
    docNumber !== '' && expected !== null && !new RegExp(`^\\d{${expected}}$`).test(docNumber)

  /*
   | Lo que la SUNAT dice de ese RUC.
   |
   | Once dígitos bien contados no bastan: `20131312955` y `20100047218` son los
   | dos válidos y son empresas distintas. Se enseña el nombre real ANTES de
   | guardar, para que quien teclea se dé cuenta solo si se equivocó — avisar
   | después de guardar llega tarde y se lee como una regañina.
   |
   | Y no se rellena la razón social por su cuenta: es el nombre legal de la
   | empresa, lo cambia una persona mirando. Hay un botón para copiarlo.
   */
  const padron = useQuery({
    queryKey: ['padron', docNumber],
    queryFn: () =>
      api.get<{ data: PadronRuc | null }>(`/tenant/padron?ruc=${encodeURIComponent(docNumber)}`),
    enabled: docType === 'RUC' && !wrongLength && /^\d{11}$/.test(docNumber),
    // El padrón reducido cambia cada pocas semanas: no hace falta repreguntarlo
    // por volver a esta pantalla.
    staleTime: 60 * 60 * 1000,
    retry: false,
  })

  const segunSunat = padron.data?.data ?? null
  const distinta =
    segunSunat !== null && legalName.trim() !== '' && !mismaEmpresa(legalName, segunSunat.razon_social)

  /*
   | El correo de facturación es obligatorio, y no por burocracia.
   |
   | De él dependen cuatro avisos que no son opcionales —impago, suspensión,
   | caducidad de una entrega y borrado de una entrega caducada—. Vaciarlo no
   | rompe nada de forma visible: simplemente el estudio deja de enterarse de
   | todo eso, y de la peor manera, porque el que avisa antes de borrar es
   | justo uno de ellos.
   |
   | Se comprueba aquí además de en el servidor para que el aviso salga
   | mientras se escribe, no al pulsar Guardar.
   */
  const emailVacio = email.trim() === ''

  const ready = legalName.trim() !== '' && docNumber !== '' && !wrongLength && !emailVacio

  const save = useMutation({
    mutationFn: () =>
      api.patch<{ data: unknown }, UpdateTenantBody>('/tenant', {
        legal_name: legalName.trim(),
        tax_doc_type: docType,
        tax_doc_number: docNumber.trim(),
        billing_email: email.trim(),
        billing_address: address.trim() === '' ? null : address.trim(),
      }),
    onSuccess: onDone,
    onError: (error) =>
      setFailure(error instanceof ApiException ? error.message : 'No se pudo guardar.'),
  })

  return (
    <Card className="p-5">
      <h2 className="text-subhead font-medium text-ink">Datos para el comprobante</h2>

      <div className="mt-5 flex max-w-prose flex-col gap-5">
        <fieldset className="border-0 p-0">
          <legend className="text-label font-semibold text-ink">Documento</legend>

          <div className="mt-3 flex flex-wrap gap-2">
            {DOC_TYPES.map((doc) => (
              <label key={doc.id} className="cursor-pointer">
                <input
                  type="radio"
                  name="tax_doc_type"
                  value={doc.id}
                  checked={docType === doc.id}
                  onChange={() => setDocType(doc.id)}
                  className="peer sr-only"
                />
                <span
                  className={cx(
                    'block rounded-control border px-3 py-2 text-label transition-colors',
                    'peer-focus-visible:ring-2 peer-focus-visible:ring-accent',
                    docType === doc.id
                      ? 'border-accent bg-accent-subtle text-accent'
                      : 'border-line text-ink-muted hover:border-line-strong',
                  )}
                >
                  {doc.label}
                </span>
              </label>
            ))}
          </div>

          <p className="mt-2 text-caption text-ink-subtle">
            {DOC_TYPES.find((doc) => doc.id === docType)?.hint}
          </p>
        </fieldset>

        <Field
          label="Número"
          value={docNumber}
          onChange={(event) =>
            setDocNumber(digitsOnly ? event.target.value.replace(/\D/g, '') : event.target.value)
          }
          inputMode={digitsOnly ? 'numeric' : 'text'}
          maxLength={20}
          error={wrongLength ? `El ${docType} tiene ${expected ?? 0} dígitos.` : null}
        />

        {/*
          Lo que dice la SUNAT de ese número, mientras se teclea.

          Que no conteste no se enseña: es una comprobación de cortesía sobre un
          servicio auxiliar, y un «no se pudo comprobar» en un formulario donde
          todo lo demás está bien es ruido que preocupa sin dar nada que hacer.
        */}
        {padron.isFetching && (
          <p className="-mt-3 text-caption text-ink-subtle">Comprobando en SUNAT…</p>
        )}

        {segunSunat !== null && (
          <div
            className={cx(
              '-mt-3 rounded-control border px-3 py-2.5',
              distinta ? 'border-warning-line bg-warning-subtle' : 'border-line bg-surface-sunken',
            )}
          >
            <p className="text-caption text-ink-subtle">Según SUNAT, ese RUC es de</p>
            <p className="text-label font-semibold text-ink">{segunSunat.razon_social}</p>

            {segunSunat.estado !== 'ACTIVO' && (
              <p className="mt-1 text-caption text-warning">
                Figura como <strong>{segunSunat.estado}</strong>. Un RUC que no está activo no puede
                recibir comprobantes.
              </p>
            )}

            {distinta && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <p className="text-caption text-ink-muted">
                  No es lo que escribiste abajo. Si el número es correcto, usa este nombre.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => setLegalName(segunSunat.razon_social)}
                  className="shrink-0"
                >
                  Usar el de SUNAT
                </Button>
              </div>
            )}

            {/*
              De cuándo es la foto que se está enseñando.

              El padrón reducido no es una base viva: es un fichero que la SUNAT
              republica cada pocos días. Sin decir de cuándo es, «según SUNAT» se
              lee como «ahora mismo», y no lo es — sobre todo cuando lo que se
              enseña discrepa de lo que el estudio acaba de escribir, que es
              justo cuando alguien va a dudar de quién tiene razón.

              Sólo se destaca si el propio padrón dice que la foto está vieja: en
              condiciones normales es un dato de contexto, no un aviso, y un
              aviso que sale siempre se ignora.
            */}
            {segunSunat.snapshot !== null && (
              <p
                className={cx(
                  'mt-2 text-caption',
                  segunSunat.snapshot.desfasada ? 'text-warning' : 'text-ink-subtle',
                )}
              >
                {segunSunat.snapshot.desfasada
                  ? `Ojo: el padrón que consultamos es del ${fechaDelPadron(segunSunat.snapshot.fecha)} y lleva sin actualizarse más de lo normal.`
                  : `Según el padrón del ${fechaDelPadron(segunSunat.snapshot.fecha)}.`}
              </p>
            )}
          </div>
        )}

        <Field
          label={docType === 'RUC' ? 'Razón social' : 'Nombre completo'}
          value={legalName}
          onChange={(event) => setLegalName(event.target.value)}
          maxLength={300}
          hint="Tal como figura en tu documento."
        />

        {/*
          El texto de ayuda decía «Opcional. Si lo dejas vacío, va al correo con
          el que entras», y no era verdad: no hay tal respaldo. Con el campo
          vacío el aviso no se manda a ningún sitio — se registra un warning en
          el log y ahí acaba.
        */}
        <Field
          label="Email de facturación"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          error={emailVacio ? 'Hace falta un correo al que escribirte.' : undefined}
          hint="Aquí llegan los avisos de cobro y los de una entrega a punto de caducar. No puede quedar vacío."
        />

        <Field
          label="Dirección fiscal"
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          maxLength={500}
          hint="Opcional."
        />

        {failure !== null && (
          <Banner tone="danger" live="alert">
            {failure}
          </Banner>
        )}

        <div className="flex flex-wrap gap-2">
          <Button disabled={!ready} busy={save.isPending && 'Guardando…'} onClick={() => save.mutate()}>
            Guardar
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </div>
    </Card>
  )
}
